import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Loader2, Share2, Copy, Check } from 'lucide-react';
import { API } from '../utils/helpers';

export const ShareModal = ({ isOpen, onClose, shareUrl, title, description }) => {
  const [copied, setCopied] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const encodedUrl = encodeURIComponent(shareUrl || '');
  const encodedText = encodeURIComponent(`${title || ''}\n${description || ''}\n`);

  const shareLinks = [
    {
      name: 'WhatsApp',
      url: `https://wa.me/?text=${encodedText}${encodedUrl}`,
      color: 'bg-green-600 hover:bg-green-700',
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      ),
    },
    {
      name: 'Telegram',
      url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      color: 'bg-sky-500 hover:bg-sky-600',
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      ),
    },
    {
      name: 'Facebook',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: 'bg-blue-600 hover:bg-blue-700',
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
    },
    {
      name: 'X',
      url: `https://twitter.com/intent/tweet?text=${encodedText}${encodedUrl}`,
      color: 'bg-[var(--bg-card)] hover:bg-gray-700',
      icon: (
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
    },
  ];

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Use portal to render modal at body level to avoid positioning issues
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="share-modal-overlay"
    >
      <div
        className="glass-card p-6 rounded-xl border border-gold-300/10 max-w-sm w-full animate-in zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
        data-testid="share-modal-content"
      >
        <h3 className="text-lg font-bold text-purple-300 mb-1">Compartir</h3>
        <p className="text-[var(--text-muted)] text-sm mb-5">{title}</p>

        <div className="grid grid-cols-4 gap-2 mb-5">
          {shareLinks.map(link => (
            <a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${link.color} text-white flex flex-col items-center gap-2 py-3 rounded-xl transition-all`}
              data-testid={`share-${link.name.toLowerCase().replace(/[^a-z]/g, '')}`}
            >
              {link.icon}
              <span className="text-xs">{link.name}</span>
            </a>
          ))}
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            value={shareUrl}
            readOnly
            className="mystic-input flex-1 text-xs"
            data-testid="share-url-input"
          />
          <button
            onClick={handleCopy}
            className={`px-3 py-2 rounded-lg transition-all ${copied ? 'bg-green-600 text-white' : 'bg-white/10 text-[var(--text-secondary)] hover:bg-white/20'}`}
            data-testid="copy-link-btn"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>

        <button onClick={onClose} className="w-full mt-4 text-[var(--text-muted)] text-sm hover:text-[var(--text-secondary)] transition-colors" data-testid="share-modal-close">
          Cerrar
        </button>
      </div>
    </div>,
    document.body
  );
};

export const ShareButton = ({ type, content, title, description }) => {
  const [showModal, setShowModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await fetch(`${API}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ type, content })
      });
      if (res.ok) {
        const data = await res.json();
        setShareUrl(`${window.location.origin}/shared/${data.share_id}`);
        setShowModal(true);
      }
    } catch (e) { console.error(e); }
    setSharing(false);
  };

  return (
    <>
      <button
        onClick={handleShare}
        disabled={sharing}
        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-400/10 text-purple-300 hover:bg-purple-600/30 transition-all text-sm"
        data-testid="share-btn"
      >
        {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
        Compartir
      </button>
      <ShareModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        shareUrl={shareUrl}
        title={title}
        description={description}
      />
    </>
  );
};
